'use client';

import type { ReactNode } from 'react';
import { Container } from '@/atoms/Container/Container';
import { cn } from '@/libs/utils/utils';
import { ResourceBookmarkButton } from './ResourceBookmarkButton';
import { ResourcePostButton } from './ResourcePostButton';
import { ResourceSavePicker } from './ResourceSavePicker';
import { ResourceTagBar, ResourceTagLabel } from './ResourceTagBar';

interface ResourceActionsProps {
  /** The external resource's link exactly as written: what is bookmarked, collected and tagged. */
  uri: string;
  /**
   * The web link "Post about this" puts in the composer, when `uri` is not one; `null` when the
   * resource has none (the button is disabled). Defaults to `uri`.
   */
  postUri?: string | null;
  /** How many public collections hold the link (Pinakes); absent for a link Pinakes has no card for. */
  collectionsCount?: number | null;
  /** The tag bar's label ("Tags on this video"). */
  tagsLabel: string;
  /**
   * The `Open in <service> ▾` control, for a card that has one: it takes the left of the action
   * row, and the tag label moves to the start of the tags below it.
   */
  openIn?: ReactNode;
  /** `large`: under the resource page's large card, with its wider padding from `sm`. */
  size?: 'default' | 'large';
}

/**
 * What closes an external resource card, in a post or atop its page: one zone under the card's one
 * divider.
 *
 * - Row A: the Open in control (else the tag label) on the left; bookmark, add to collection with
 *   the public count, and post about this on the right, on one line at any width: the actions keep
 *   their size and Open in shrinks to what is left (on a phone it shows the site alone, its label
 *   truncated; "Open in" stays in its accessible name).
 * - Row B: the tag label when Open in took its place, then the tags and "+".
 *
 * The zone is all controls, so a click on it never counts as a click through the card.
 */
export function ResourceActions({
  uri,
  postUri = uri,
  collectionsCount,
  tagsLabel,
  openIn,
  size = 'default',
}: ResourceActionsProps) {
  return (
    <Container
      overrideDefaults
      data-resource-controls
      data-testid="resource-actions"
      data-size={size}
      className={cn('flex min-w-0 flex-col gap-2 px-4 py-3', size === 'large' && 'sm:gap-3 sm:px-6 sm:py-4')}
    >
      <Container
        overrideDefaults
        data-testid="resource-action-row"
        className="flex min-w-0 items-center justify-between gap-3"
      >
        {openIn ?? <ResourceTagLabel label={tagsLabel} />}
        <Container overrideDefaults className="ml-auto flex shrink-0 items-center gap-1">
          <ResourceBookmarkButton uri={uri} />
          <ResourceSavePicker uri={uri} collectionsCount={collectionsCount} />
          <ResourcePostButton uri={postUri} />
        </Container>
      </Container>
      <ResourceTagBar uri={uri} label={openIn ? tagsLabel : undefined} />
    </Container>
  );
}
